
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import { projects } from '../../mocks/projects';
import { talents } from '../../mocks/talents';
import { clients } from '../../mocks/clients';

export default function ProjectsPage() {
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('list');

  const getStatusColor = (status: string) => {
    switch (status) {
      case '招聘中': return 'bg-blue-100 text-blue-800';
      case '面试中': return 'bg-orange-100 text-orange-800';
      case '已完成': return 'bg-green-100 text-green-800';
      case '暂停': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case '高': return 'bg-red-100 text-red-800';
      case '中': return 'bg-yellow-100 text-yellow-800';
      case '低': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(project => project.status === filter);

  const getAssignedTalentNames = (talentIds: number[]) => {
    return talentIds.map(id => {
      const talent = talents.find(t => t.id === id);
      return talent ? talent.name : '';
    }).filter(Boolean);
  };

  const renderProjectDetail = () => {
    if (!selectedProject) return null;

    const assignedTalentNames = getAssignedTalentNames(selectedProject.assignedTalents);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-briefcase-line text-blue-600 text-2xl"></i>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{selectedProject.title}</h2>
                  <p className="text-gray-600">{selectedProject.clientName}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedProject(null)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
          </div>
          
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">基本信息</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-500">客户:</span>
                    <span className="font-medium">{selectedProject.clientName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">状态:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedProject.status)}`}>
                      {selectedProject.status}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">优先级:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(selectedProject.priority)}`}>
                      {selectedProject.priority}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">薪资:</span>
                    <span className="font-medium">{selectedProject.salary}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">地点:</span>
                    <span className="font-medium">{selectedProject.location}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">经验要求:</span>
                    <span className="font-medium">{selectedProject.experienceRequired}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">项目进度</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-500">招聘人数:</span>
                    <span className="font-medium">{selectedProject.headcount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">当前申请:</span>
                    <span className="font-medium">{selectedProject.currentApplications}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">开始时间:</span>
                    <span className="font-medium">{selectedProject.startDate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">截止时间:</span>
                    <span className="font-medium">{selectedProject.deadline}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">负责人:</span>
                    <span className="font-medium">{selectedProject.operator}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">招聘漏斗</h3>
                <div className="space-y-3">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-blue-700">简历投递</span>
                      <span className="font-bold text-blue-800">{selectedProject.currentApplications}</span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2 mt-2">
                      <div className="bg-blue-600 h-2 rounded-full" style={{width: '100%'}}></div>
                    </div>
                  </div>
                  <div className="bg-orange-50 p-3 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-orange-700">面试邀请</span>
                      <span className="font-bold text-orange-800">{Math.floor(selectedProject.currentApplications * 0.6)}</span>
                    </div>
                    <div className="w-full bg-orange-200 rounded-full h-2 mt-2">
                      <div className="bg-orange-600 h-2 rounded-full" style={{width: '60%'}}></div>
                    </div>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-purple-700">Offer发放</span>
                      <span className="font-bold text-purple-800">{Math.floor(selectedProject.currentApplications * 0.3)}</span>
                    </div>
                    <div className="w-full bg-purple-200 rounded-full h-2 mt-2">
                      <div className="bg-purple-600 h-2 rounded-full" style={{width: '30%'}}></div>
                    </div>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-green-700">成功入职</span>
                      <span className="font-bold text-green-800">{Math.floor(selectedProject.currentApplications * 0.15)}</span>
                    </div>
                    <div className="w-full bg-green-200 rounded-full h-2 mt-2">
                      <div className="bg-green-600 h-2 rounded-full" style={{width: '15%'}}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">职位描述</h3>
              <p className="text-gray-700 leading-relaxed">{selectedProject.description}</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">职位要求</h3>
              <ul className="space-y-2">
                {selectedProject.requirements.map((req: string, index: number) => (
                  <li key={index} className="flex items-start space-x-2">
                    <i className="ri-check-line text-green-600 mt-0.5"></i>
                    <span className="text-gray-700">{req}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">候选人管理</h3>
              {assignedTalentNames.length > 0 ? (
                <div className="space-y-3">
                  {assignedTalentNames.map((name, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <i className="ri-user-fill text-blue-600 text-sm"></i>
                        </div>
                        <span className="font-medium">{name}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs">面试中</span>
                        <Button size="sm" variant="outline">查看详情</Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">暂无分配人才</p>
              )}
            </div>

            <div className="flex justify-end space-x-4">
              <Button variant="outline">编辑项目</Button>
              <Button variant="outline">AI推荐人才</Button>
              <Button>分配人才</Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAIFeatures = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">AI智能功能</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="智能人才匹配">
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <i className="ri-brain-line text-blue-600"></i>
                <span className="font-medium text-blue-800">AI匹配引擎</span>
              </div>
              <div className="text-sm text-blue-700">
                为 <span className="font-bold">{projects.filter(p => p.status === '招聘中').length}</span> 个活跃项目匹配人才
              </div>
            </div>
            <div className="space-y-3">
              {projects.filter(p => p.status === '招聘中').slice(0, 3).map((project, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{project.title}</span>
                    <span className="text-sm text-blue-600">95%匹配</span>
                  </div>
                  <div className="text-sm text-gray-600">推荐 {Math.floor(Math.random() * 10) + 5} 位候选人</div>
                  <div className="flex justify-end mt-2">
                    <Button size="sm" variant="outline">查看推荐</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <Card title="市场候选人搜寻">
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <i className="ri-search-line text-green-600"></i>
                <span className="font-medium text-green-800">外部搜寻</span>
              </div>
              <div className="text-sm text-green-700">
                已搜寻 <span className="font-bold">1,234</span> 位市场候选人
              </div>
            </div>
            <div className="space-y-3">
              {[
                { platform: '智联招聘', count: 456, quality: '高' },
                { platform: '猎聘网', count: 321, quality: '中' },
                { platform: '前程无忧', count: 287, quality: '高' },
                { platform: 'BOSS直聘', count: 170, quality: '中' }
              ].map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-900">{item.platform}</div>
                    <div className="text-sm text-gray-600">{item.count} 位候选人</div>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      item.quality === '高' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {item.quality}质量
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      <Card title="职位发布智能化">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-3">系统人才库匹配</h4>
            <div className="space-y-3">
              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="text-sm font-medium text-blue-800">自动匹配算法</div>
                <div className="text-xs text-blue-600 mt-1">基于技能、经验、地点等维度智能匹配</div>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg">
                <div className="text-sm font-medium text-purple-800">AI推荐排序</div>
                <div className="text-xs text-purple-600 mt-1">根据匹配度和成功率智能排序</div>
              </div>
              <Button className="w-full" size="sm">
                <i className="ri-magic-line mr-2"></i>
                开始智能匹配
              </Button>
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-3">市场候选人推荐</h4>
            <div className="space-y-3">
              <div className="bg-green-50 p-3 rounded-lg">
                <div className="text-sm font-medium text-green-800">外部平台搜索</div>
                <div className="text-xs text-green-600 mt-1">从各大招聘平台搜索潜在候选人</div>
              </div>
              <div className="bg-orange-50 p-3 rounded-lg">
                <div className="text-sm font-medium text-orange-800">公司推荐引擎</div>
                <div className="text-xs text-orange-600 mt-1">推荐目标公司的优秀人才</div>
              </div>
              <Button className="w-full" size="sm" variant="outline">
                <i className="ri-search-2-line mr-2"></i>
                启动外部搜寻
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );

  const renderProjectList = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">项目管理</h1>
          <p className="text-gray-600">管理所有招聘项目和职位信息</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <i className="ri-add-line mr-2"></i>
          新建项目
        </Button>
      </div>

      {/* 筛选和统计 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600 mb-1">{projects.length}</div>
            <div className="text-sm text-gray-600">总项目数</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600 mb-1">
              {projects.filter(p => p.status === '招聘中').length}
            </div>
            <div className="text-sm text-gray-600">招聘中</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600 mb-1">
              {projects.filter(p => p.status === '面试中').length}
            </div>
            <div className="text-sm text-gray-600">面试中</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600 mb-1">
              {projects.filter(p => p.priority === '高').length}
            </div>
            <div className="text-sm text-gray-600">高优先级</div>
          </div>
        </Card>
      </div>

      {/* 筛选条件 */}
      <Card>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700">状态筛选:</span>
            <div className="flex space-x-2">
              {['all', '招聘中', '面试中', '已完成', '暂停'].map(status => (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap ${
                    filter === status
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {status === 'all' ? '全部' : status}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <Input placeholder="搜索项目..." className="w-64" />
            <Button variant="outline">
              <i className="ri-filter-line mr-2"></i>
              筛选
            </Button>
          </div>
        </div>
      </Card>

      {/* 项目列表 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredProjects.map(project => (
          <Card key={project.id} className="cursor-pointer hover:shadow-lg transition-shadow duration-200">
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{project.title}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                      {project.status}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(project.priority)}`}>
                      {project.priority}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-2">{project.clientName}</p>
                  <p className="text-sm text-gray-500 mb-3 line-clamp-2">{project.description}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center space-x-2">
                  <i className="ri-money-dollar-circle-line text-green-600"></i>
                  <span>{project.salary}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="ri-map-pin-line text-gray-600"></i>
                  <span>{project.location}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="ri-team-line text-blue-600"></i>
                  <span>招聘 {project.headcount} 人</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="ri-user-line text-purple-600"></i>
                  <span>{project.currentApplications} 人申请</span>
                </div>
              </div>

              {/* 进度条 */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">招聘进度</span>
                  <span className="text-gray-900">{Math.floor((project.currentApplications / project.headcount) * 100)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full" 
                    style={{width: `${Math.min((project.currentApplications / project.headcount) * 100, 100)}%`}}
                  ></div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-500">
                  负责人: {project.operator} · 截止: {project.deadline}
                </div>
                <div className="flex space-x-2">
                  <Button size="sm" variant="outline" onClick={() => setSelectedProject(project)}>
                    查看详情
                  </Button>
                  <Button size="sm">管理</Button>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {filteredProjects.length === 0 && (
        <Card>
          <div className="text-center py-12">
            <i className="ri-briefcase-line text-4xl text-gray-400 mb-4"></i>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">暂无项目</h3>
            <p className="text-gray-500">请创建新的招聘项目</p>
          </div>
        </Card>
      )}
    </div>
  );

  const renderJobManagement = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">职位建档 - 完整信息管理</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="职位基本信息">
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <i className="ri-briefcase-line text-blue-600"></i>
                <span className="font-medium text-blue-800">职位档案管理</span>
              </div>
              <div className="text-sm text-blue-700">
                已建档 <span className="font-bold">{projects.length}</span> 个职位的完整信息
              </div>
            </div>
            
            <div className="space-y-3">
              {projects.slice(0, 3).map((project, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">{project.title}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                      {project.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">客户:</span>
                      <span className="font-medium ml-1">{project.clientName}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">薪资:</span>
                      <span className="font-medium ml-1">{project.salary}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">地点:</span>
                      <span className="font-medium ml-1">{project.location}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">负责人:</span>
                      <span className="font-medium ml-1">{project.operator}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">汇报对象:</span>
                      <span className="font-medium ml-1">技术总监</span>
                    </div>
                    <div>
                      <span className="text-gray-500">团队规模:</span>
                      <span className="font-medium ml-1">15人</span>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">协作人:</span> 李经理、王顾问、张专员
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <Card title="JD详细信息">
          <div className="space-y-4">
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <i className="ri-file-list-line text-purple-600"></i>
                <span className="font-medium text-purple-800">JD智能解析</span>
              </div>
              <div className="text-sm text-purple-700">
                AI自动提取JD关键信息，结构化存储
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">高级前端工程师 - 阿里巴巴</h4>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-gray-700">职位描述:</span>
                    <p className="text-sm text-gray-600 mt-1">
                      负责淘宝前端业务开发，参与大型项目架构设计，优化用户体验，
                      与产品、设计、后端团队紧密协作，推动技术创新。
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700">核心要求:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {['React 3年+', 'TypeScript', '大型项目经验', '本科以上', '大厂背景'].map((req, i) => (
                        <span key={i} className="bg-red-50 text-red-700 px-2 py-1 rounded text-xs">
                          {req}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700">加分项:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {['Node.js', '微前端', '性能优化', '团队管理'].map((plus, i) => (
                        <span key={i} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                          {plus}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">工作地点:</span>
                      <span className="font-medium ml-1">杭州</span>
                    </div>
                    <div>
                      <span className="text-gray-500">汇报对象:</span>
                      <span className="font-medium ml-1">前端技术总监</span>
                    </div>
                    <div>
                      <span className="text-gray-500">团队规模:</span>
                      <span className="font-medium ml-1">前端团队20人</span>
                    </div>
                    <div>
                      <span className="text-gray-500">发展空间:</span>
                      <span className="font-medium ml-1">技术专家路线</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* 职位分类统计 */}
      <Card title="职位分类与统计分析">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-3">技术栈分布</h4>
            <div className="space-y-2">
              {[
                { tech: 'React', count: 15, color: 'blue' },
                { tech: 'Java', count: 12, color: 'orange' },
                { tech: 'Python', count: 8, color: 'green' },
                { tech: 'Vue', count: 6, color: 'purple' },
                { tech: 'Go', count: 4, color: 'red' }
              ].map((item, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center space-x-2">
                    <span className={`w-3 h-3 bg-${item.color}-500 rounded-full`}></span>
                    <span className="text-sm text-gray-700">{item.tech}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{item.count}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-3">级别分布</h4>
            <div className="space-y-2">
              {[
                { level: '高级', count: 18, color: 'red' },
                { level: '中级', count: 22, color: 'orange' },
                { level: '初级', count: 8, color: 'green' },
                { level: '专家', count: 5, color: 'purple' }
              ].map((item, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center space-x-2">
                    <span className={`w-3 h-3 bg-${item.color}-500 rounded-full`}></span>
                    <span className="text-sm text-gray-700">{item.level}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{item.count}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-3">紧急程度</h4>
            <div className="space-y-2">
              {[
                { urgency: '紧急', count: 8, color: 'red' },
                { urgency: '一般', count: 28, color: 'yellow' },
                { urgency: '不急', count: 12, color: 'green' }
              ].map((item, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center space-x-2">
                    <span className={`w-3 h-3 bg-${item.color}-500 rounded-full`}></span>
                    <span className="text-sm text-gray-700">{item.urgency}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{item.count}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-3">招聘进度</h4>
            <div className="space-y-2">
              {[
                { stage: '招聘中', count: 15, color: 'blue' },
                { stage: '面试中', count: 8, color: 'orange' },
                { stage: '已完成', count: 12, color: 'green' },
                { stage: '暂停', count: 3, color: 'gray' }
              ].map((item, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center space-x-2">
                    <span className={`w-3 h-3 bg-${item.color}-500 rounded-full`}></span>
                    <span className="text-sm text-gray-700">{item.stage}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{item.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );

  const renderSmartInitialization = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">智能初始化 - AI匹配与推荐</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="内部人才库智能匹配">
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <i className="ri-brain-line text-blue-600"></i>
                <span className="font-medium text-blue-800">AI匹配引擎</span>
              </div>
              <div className="text-sm text-blue-700">
                为职位匹配 <span className="font-bold">156</span> 位内部候选人
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">高级前端工程师 - 阿里巴巴</h4>
                <Button size="sm">
                  <i className="ri-magic-line mr-2"></i>
                  开始匹配
                </Button>
              </div>
              
              {[
                {
                  name: '张三',
                  match: 95,
                  skills: ['React', 'TypeScript', '大型项目'],
                  experience: '5年',
                  currentCompany: '腾讯',
                  reason: '技能完全匹配，大厂背景，项目经验丰富',
                  risk: '薪资期望可能偏高'
                },
                {
                  name: '李四',
                  match: 88,
                  skills: ['Vue', 'React', '前端架构'],
                  experience: '4年',
                  currentCompany: '字节跳动',
                  reason: '技术栈匹配，有架构经验',
                  risk: 'Vue背景，需要React适应期'
                },
                {
                  name: '王五',
                  match: 82,
                  skills: ['React', 'Node.js', '全栈'],
                  experience: '3年',
                  currentCompany: '美团',
                  reason: '技术能力强，全栈背景加分',
                  risk: '经验略少，级别可能不够'
                }
              ].map((candidate, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                        <i className="ri-user-fill text-gray-600"></i>
                      </div>
                      <div>
                        <h5 className="font-medium text-gray-900">{candidate.name}</h5>
                        <p className="text-sm text-gray-600">{candidate.experience} · {candidate.currentCompany}</p>
                      </div>
                    </div>
                    <span className="text-lg font-bold text-green-600">{candidate.match}%</span>
                  </div>
                  <div className="mb-2">
                    <div className="flex flex-wrap gap-1">
                      {candidate.skills.map((skill, i) => (
                        <span key={i} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="bg-green-50 p-2 rounded text-sm text-green-800 mb-2">
                    <strong>推荐理由:</strong> {candidate.reason}
                  </div>
                  <div className="bg-yellow-50 p-2 rounded text-sm text-yellow-800 mb-3">
                    <strong>潜在风险:</strong> {candidate.risk}
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button size="sm" variant="outline">查看详情</Button>
                    <Button size="sm">推荐给客户</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <Card title="外部市场搜寻">
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <i className="ri-search-line text-green-600"></i>
                <span className="font-medium text-green-800">市场搜寻引擎</span>
              </div>
              <div className="text-sm text-green-700">
                从外部平台搜寻到 <span className="font-bold">1,234</span> 位潜在候选人
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">目标公司推荐</h4>
                <Button size="sm" variant="outline">
                  <i className="ri-search-2-line mr-2"></i>
                  启动搜寻
                </Button>
              </div>
              
              {[
                {
                  company: '字节跳动',
                  candidates: 45,
                  match: 88,
                  reasons: ['技术栈相似', '业务场景接近', '人才质量高'],
                  difficulty: 'medium',
                  avgSalary: '30K-45K'
                },
                {
                  company: '美团',
                  candidates: 32,
                  match: 82,
                  reasons: ['前端团队强', '技术氛围好', '项目经验丰富'],
                  difficulty: 'high',
                  avgSalary: '25K-40K'
                },
                {
                  company: '滴滴出行',
                  candidates: 28,
                  match: 75,
                  reasons: ['大型项目经验', '高并发处理', '用户体验重视'],
                  difficulty: 'low',
                  avgSalary: '28K-42K'
                }
              ].map((target, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h5 className="font-medium text-gray-900">{target.company}</h5>
                      <p className="text-sm text-gray-600">{target.candidates}位候选人 · 匹配度{target.match}%</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      target.difficulty === 'high' ? 'bg-red-100 text-red-800' :
                      target.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {target.difficulty === 'high' ? '高难度' : 
                       target.difficulty === 'medium' ? '中难度' : '低难度'}
                    </span>
                  </div>
                  <div className="mb-2">
                    <span className="text-sm font-medium text-gray-700">推荐原因:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {target.reasons.map((reason, i) => (
                        <span key={i} className="bg-purple-50 text-purple-700 px-2 py-1 rounded text-xs">
                          {reason}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 mb-3">
                    <span className="font-medium">平均薪资:</span> {target.avgSalary}
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button size="sm" variant="outline">查看候选人</Button>
                    <Button size="sm">开始挖掘</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      {/* 匹配策略配置 */}
      <Card title="匹配策略与二次筛选">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-3">匹配权重配置</h4>
            <div className="space-y-3">
              {[
                { factor: '技能匹配', weight: 35, adjustable: true },
                { factor: '经验年限', weight: 25, adjustable: true },
                { factor: '公司背景', weight: 20, adjustable: true },
                { factor: '学历要求', weight: 10, adjustable: true },
                { factor: '地域匹配', weight: 10, adjustable: true }
              ].map((item, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-700">{item.factor}</span>
                    <span className="text-sm font-medium text-blue-600">{item.weight}%</span>
                  </div>
                  {item.adjustable && (
                    <input 
                      type="range" 
                      min="0" 
                      max="50" 
                      defaultValue={item.weight} 
                      className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-3">顾问反馈机制</h4>
            <div className="space-y-3">
              <div className="p-3 bg-blue-50 rounded">
                <h5 className="font-medium text-blue-800 mb-2">反馈统计</h5>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <div className="font-bold text-blue-600">85%</div>
                    <div className="text-blue-700">推荐准确率</div>
                  </div>
                  <div>
                    <div className="font-bold text-blue-600">156</div>
                    <div className="text-blue-700">反馈样本</div>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                {[
                  { feedback: '技能匹配准确', count: 45, type: 'positive' },
                  { feedback: '经验要求偏高', count: 23, type: 'negative' },
                  { feedback: '薪资期望合理', count: 38, type: 'positive' },
                  { feedback: '地域不匹配', count: 12, type: 'negative' }
                ].map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center space-x-2">
                      <span className={`w-2 h-2 rounded-full ${
                        item.type === 'positive' ? 'bg-green-500' : 'bg-red-500'
                      }`}></span>
                      <span className="text-sm text-gray-700">{item.feedback}</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-3">二次筛选规则</h4>
            <div className="space-y-3">
              <div className="p-3 bg-yellow-50 rounded">
                <h5 className="font-medium text-yellow-800 mb-2">自动筛选规则</h5>
                <div className="space-y-2 text-sm text-yellow-700">
                  <div>• 匹配度低于70%自动过滤</div>
                  <div>• 薪资期望超出预算20%标记</div>
                  <div>• 地域不匹配降低优先级</div>
                  <div>• 重复推荐自动去重</div>
                </div>
              </div>
              <div className="space-y-2">
                <Button variant="outline" className="w-full justify-start" size="sm">
                  <i className="ri-settings-line mr-2"></i>
                  配置筛选规则
                </Button>
                <Button variant="outline" className="w-full justify-start" size="sm">
                  <i className="ri-refresh-line mr-2"></i>
                  重新匹配
                </Button>
                <Button variant="outline" className="w-full justify-start" size="sm">
                  <i className="ri-download-line mr-2"></i>
                  导出候选人
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );

  const renderVisualization = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">可视化追踪 - 职位漏斗与预测</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="职位漏斗图">
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <i className="ri-funnel-line text-blue-600"></i>
                <span className="font-medium text-blue-800">招聘漏斗分析</span>
              </div>
              <div className="text-sm text-blue-700">
                实时追踪 <span className="font-bold">{projects.length}</span> 个职位的招聘进度
              </div>
            </div>
            
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900">高级前端工程师 - 阿里巴巴</h4>
              <div className="space-y-3">
                {[
                  { stage: '简历投递', count: 120, percentage: 100, color: 'blue', conversion: 100 },
                  { stage: '简历筛选', count: 85, percentage: 71, color: 'indigo', conversion: 71 },
                  { stage: '电话沟通', count: 65, percentage: 54, color: 'purple', conversion: 76 },
                  { stage: '技术面试', count: 45, percentage: 38, color: 'pink', conversion: 69 },
                  { stage: 'HR面试', count: 28, percentage: 23, color: 'red', conversion: 62 },
                  { stage: 'Offer发放', count: 18, percentage: 15, color: 'orange', conversion: 64 },
                  { stage: '成功入职', count: 12, percentage: 10, color: 'green', conversion: 67 }
                ].map((stage, index) => (
                  <div key={index} className="relative">
                    <div className={`bg-${stage.color}-100 rounded-lg p-4 relative overflow-hidden`}>
                      <div className={`absolute inset-0 bg-${stage.color}-500 opacity-20`} style={{width: `${stage.percentage}%`}}></div>
                      <div className="relative flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-900">{stage.stage}</div>
                          <div className="text-sm text-gray-600">转化率: {stage.conversion}%</div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-gray-900">{stage.count}</div>
                          <div className="text-sm text-gray-600">{stage.percentage}%</div>
                        </div>
                      </div>
                    </div>
                    {index < 6 && (
                      <div className="flex justify-center my-2">
                        <i className="ri-arrow-down-line text-gray-400"></i>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>

        <Card title="渠道效果分析">
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <i className="ri-line-chart-line text-green-600"></i>
                <span className="font-medium text-green-800">渠道效果统计</span>
              </div>
              <div className="text-sm text-green-700">
                分析 <span className="font-bold">5</span> 个主要招聘渠道的效果
              </div>
            </div>
            
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">渠道效果对比</h4>
              {[
                {
                  channel: '站内人才库',
                  candidates: 156,
                  interviews: 89,
                  offers: 34,
                  hires: 28,
                  cost: '¥0',
                  quality: 'high'
                },
                {
                  channel: '智联招聘',
                  candidates: 234,
                  interviews: 98,
                  offers: 28,
                  hires: 22,
                  cost: '¥15,000',
                  quality: 'medium'
                },
                {
                  channel: '猎聘网',
                  candidates: 89,
                  interviews: 56,
                  offers: 23,
                  hires: 19,
                  cost: '¥25,000',
                  quality: 'high'
                },
                {
                  channel: '内推',
                  candidates: 45,
                  interviews: 38,
                  offers: 18,
                  hires: 16,
                  cost: '¥8,000',
                  quality: 'high'
                },
                {
                  channel: '校园招聘',
                  candidates: 78,
                  interviews: 45,
                  offers: 12,
                  hires: 10,
                  cost: '¥12,000',
                  quality: 'medium'
                }
              ].map((channel, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <h5 className="font-medium text-gray-900">{channel.channel}</h5>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        channel.quality === 'high' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {channel.quality === 'high' ? '高质量' : '中质量'}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-blue-600">{channel.cost}</span>
                  </div>
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div className="text-center">
                      <div className="font-bold text-blue-600">{channel.candidates}</div>
                      <div className="text-gray-600">候选人</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-purple-600">{channel.interviews}</div>
                      <div className="text-gray-600">面试</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-orange-600">{channel.offers}</div>
                      <div className="text-gray-600">Offer</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-green-600">{channel.hires}</div>
                      <div className="text-gray-600">入职</div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">转化率:</span>
                      <span className="font-medium">{Math.round((channel.hires / channel.candidates) * 100)}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      {/* 周期预测与预警 */}
      <Card title="周期预测与AI预警">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-3">招聘周期预测</h4>
            <div className="space-y-3">
              <div className="bg-blue-50 p-3 rounded-lg">
                <h5 className="font-medium text-blue-800 mb-2">AI预测模型</h5>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <div className="font-bold text-blue-600">28天</div>
                    <div className="text-blue-700">预计周期</div>
                  </div>
                  <div>
                    <div className="font-bold text-blue-600">85%</div>
                    <div className="text-blue-700">预测准确率</div>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                {[
                  { position: '高级前端', predicted: 25, actual: 28, status: 'normal' },
                  { position: 'Java后端', predicted: 32, actual: 35, status: 'delay' },
                  { position: '产品经理', predicted: 45, actual: 42, status: 'ahead' },
                  { position: 'UI设计师', predicted: 22, actual: 25, status: 'normal' }
                ].map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{item.position}</div>
                      <div className="text-xs text-gray-500">预测{item.predicted}天 / 实际{item.actual}天</div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      item.status === 'delay' ? 'bg-red-100 text-red-800' :
                      item.status === 'ahead' ? 'bg-green-100 text-green-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {item.status === 'delay' ? '延期' : item.status === 'ahead' ? '提前' : '正常'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-3">风险预警</h4>
            <div className="space-y-3">
              <div className="bg-red-50 p-3 rounded-lg">
                <h5 className="font-medium text-red-800 mb-2">预警信号</h5>
                <div className="text-sm text-red-700">
                  发现 <span className="font-bold">3</span> 个高风险项目
                </div>
              </div>
              <div className="space-y-2">
                {[
                  {
                    project: 'Java后端工程师-腾讯',
                    risk: '候选人流失率高',
                    level: 'high',
                    suggestion: '调整薪资预算或降低要求'
                  },
                  {
                    project: '产品经理-字节跳动',
                    risk: '面试通过率低',
                    level: 'medium',
                    suggestion: '优化候选人筛选标准'
                  },
                  {
                    project: 'UI设计师-美团',
                    risk: '客户响应慢',
                    level: 'low',
                    suggestion: '主动跟进客户反馈'
                  }
                ].map((warning, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900 text-sm">{warning.project}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        warning.level === 'high' ? 'bg-red-100 text-red-800' :
                        warning.level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {warning.level === 'high' ? '高风险' : warning.level === 'medium' ? '中风险' : '低风险'}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 mb-2">{warning.risk}</div>
                    <div className="text-xs text-blue-600">{warning.suggestion}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-3">优化建议</h4>
            <div className="space-y-3">
              <div className="bg-green-50 p-3 rounded-lg">
                <h5 className="font-medium text-green-800 mb-2">AI优化建议</h5>
                <div className="space-y-1 text-sm text-green-700">
                  <div>• 技术面试环节耗时较长，建议优化流程</div>
                  <div>• 薪资谈判失败率高，需提前沟通</div>
                  <div>• 候选人体验有待提升</div>
                </div>
              </div>
              <div className="space-y-2">
                <Button variant="outline" className="w-full justify-start" size="sm">
                  <i className="ri-lightbulb-line mr-2"></i>
                  查看详细分析
                </Button>
                <Button variant="outline" className="w-full justify-start" size="sm">
                  <i className="ri-settings-line mr-2"></i>
                  优化流程配置
                </Button>
                <Button variant="outline" className="w-full justify-start" size="sm">
                  <i className="ri-alarm-line mr-2"></i>
                  设置预警规则
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* 标签页导航 */}
        <div className="mb-8">
          <nav className="flex space-x-8">
            {[
              { id: 'list', name: '项目列表', icon: 'ri-briefcase-line' },
              { id: 'job', name: '职位建档', icon: 'ri-file-list-line' },
              { id: 'smart', name: '智能初始化', icon: 'ri-brain-line' },
              { id: 'visualization', name: '可视化追踪', icon: 'ri-bar-chart-line' },
              { id: 'ai', name: 'AI功能', icon: 'ri-robot-line' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2 border-b-2 font-medium text-sm cursor-pointer whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className={`${tab.icon} text-base`}></i>
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* 标签页内容 */}
        {activeTab === 'list' && renderProjectList()}
        {activeTab === 'job' && renderJobManagement()}
        {activeTab === 'smart' && renderSmartInitialization()}
        {activeTab === 'visualization' && renderVisualization()}
        {activeTab === 'ai' && renderAIFeatures()}
      </div>

      {renderProjectDetail()}
    </div>
  );
}
